import { useCallback, useEffect, useMemo, useRef } from 'react';

import { FinancialDatasetRequest } from '@futrli-sdk/predict';

import { areFinQueriesEqual, widgetHasTwoAxes } from '../../../utils-data';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';
import { useReportBuilderContext } from '../../../report-builder.context';
import { LayoutData, WIDGET_TYPES } from '../../../types';
import { useI18nConstants } from '../../../use-i18n-constants';

interface WidgetQuery {
  i: string;
  type: WIDGET_TYPES | undefined;
  queries: FinancialDatasetRequest[];
}

export const useLatestWidgetDataQueries = () => {
  const {
    getWidgetType,
    currentCard,
    currentCardId,
    allFinFields,
    forecasts,
    isTemplate,
    isWorkspaceTemplate,
    financialConfig,
    reportData,
    updateFinDataForWidget,
    emptyFinDataForWidget,
    layoutFinData,
    rowFormulaData,
    futrliFormulaData
  } = useReportBuilderContext();
  const { getFinDataQueryForWidget } = useI18nConstants();

  const { isConsolidation } = usePredictionsInfoContext();
  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;
  const widgetType = currentCardId ? getWidgetType() : undefined;
  const initialCardData = useMemo(() => reportData.pageLayoutData.find(e => e.i === currentCard?.i), [
    reportData,
    currentCard
  ]);

  const getLatestWidgetQuery = useCallback(
    (cardData?: LayoutData) => {
      if (cardData) {
        return getFinDataQueryForWidget(
          cardData,
          allFinFields,
          [...(rowFormulaData || []), ...(futrliFormulaData || [])],
          forecasts,
          true,
          isFutrliTemplate,
          financialConfig,
          isConsolidation
        );
      }
      return [];
    },
    [
      allFinFields,
      financialConfig,
      forecasts,
      futrliFormulaData,
      isConsolidation,
      isFutrliTemplate,
      rowFormulaData,
      getFinDataQueryForWidget
    ]
  );

  const prevWidgetQuery = useRef<WidgetQuery>({
    i: currentCard?.i || '',
    type: widgetType,
    queries: getLatestWidgetQuery(initialCardData)
  });

  // Set latest widget query data in context to force data to load
  useEffect(() => {
    if (!currentCard) {
      return;
    }

    // we get a list of queries (one for each dataset) for selected card
    // returns an empty array if widget doesn't have enough data to construct a query
    const newQuery = getLatestWidgetQuery(currentCard);

    const hasCurrentCardChanged = prevWidgetQuery.current.i !== currentCard.i;
    const hasNumberOfAxisChanged = widgetHasTwoAxes(prevWidgetQuery.current.type) !== widgetHasTwoAxes(widgetType);
    const hasQueryDataChanged = !areFinQueriesEqual(newQuery, prevWidgetQuery.current.queries);
    // Switching card or axis count normally reuses the data already loaded for the widget.
    // If we don't hold any, we still need to fetch it, otherwise the widget renders empty.
    const hasLoadedData = !!layoutFinData[currentCard.i];

    if (newQuery.length) {
      // if we switch card, we need to construct a query for the new card
      if ((hasCurrentCardChanged || hasNumberOfAxisChanged) && hasLoadedData) {
        prevWidgetQuery.current = {
          i: currentCard.i,
          type: widgetType,
          queries: getLatestWidgetQuery(initialCardData)
        };
        // if we are on the same card, we compare new query to previous one, we only fetch financial data if query changes
      } else if (hasCurrentCardChanged || hasNumberOfAxisChanged || hasQueryDataChanged) {
        prevWidgetQuery.current = { i: currentCard.i, type: widgetType, queries: newQuery };
        updateFinDataForWidget(currentCard.i, newQuery);
      }
    } else {
      // widget doesn't have enough data to construct a query and get financial data
      // we empty previous data for current card
      // we update the ref for prevQuery to be able to compare against it next time
      emptyFinDataForWidget(currentCard.i);
      prevWidgetQuery.current = { i: currentCard.i, type: widgetType, queries: newQuery };
    }
  }, [
    currentCard,
    emptyFinDataForWidget,
    getLatestWidgetQuery,
    initialCardData,
    layoutFinData,
    updateFinDataForWidget,
    widgetType
  ]);
};
